/**
 * KK-809 / KK-1507 / KK-522 · Satış noktaları, personel ataması ve vekalet.
 *
 * AK-19 — DEPO SATIŞ YAPMAZ. Bu bir tercih değil, veritabanı kısıtıdır
 * (ck_depo_satmaz / ck_depo_turu): depo türündeki nokta cüzdan türü alamaz ve
 * ona terminal bağlanamaz. Ekran bunu kullanıcıya hata mesajıyla değil, form
 * davranışıyla öğretir.
 *
 * KK-522 — Vekalet başlangıç/bitiş tarihlidir ve süresi dolunca kendiliğinden
 * düşer; ayrı bir temizlik işlemi gerekmez. "Geçerli" işareti yetki
 * çözümleyicisiyle AYNI tarih süzgecini kullanır.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  Buton,
  Kart,
  Rozet,
  Secim,
  Tablo,
  TabloBaslik,
  TabloGovde,
  Uyari,
} from "@kantin/ui";
import { CuzdanTuru, FisPolitikasi, NoktaTuru } from "@kantin/shared";
import { apiOku, apiYaz } from "./api.js";
import { usePersoneller } from "./kancalar.js";
import type { Nokta, Personel } from "./kancalar.js";
import {
  IslemDurumu,
  Sayfa,
  SayfaBasligi,
  SorguDurumu,
  tarihGoster,
} from "./ortak.js";

const NOKTA_TURU_ADI: Record<number, string> = {
  [NoktaTuru.KANTIN]: "Kantin",
  [NoktaTuru.YEMEKHANE]: "Yemekhane",
  [NoktaTuru.KIRTASIYE]: "Kırtasiye",
  [NoktaTuru.OTOMAT]: "Otomat",
  [NoktaTuru.DEPO]: "Depo",
};

const CUZDAN_TURU_ADI: Record<number, string> = {
  [CuzdanTuru.KANTIN]: "Kantin cüzdanı",
  [CuzdanTuru.YEMEKHANE]: "Yemekhane cüzdanı",
  [CuzdanTuru.DEPOZITO]: "Depozito cüzdanı",
};

const FIS_POLITIKASI_ADI: Record<number, string> = {
  [FisPolitikasi.KAPALI]: "Kapalı",
  [FisPolitikasi.OPSIYONEL]: "Opsiyonel (görevli seçer)",
  [FisPolitikasi.ZORUNLU]: "Zorunlu",
};

const ATAMA_ROLU_ADI: Record<number, string> = {
  1: "Kasiyer",
  2: "Nokta sorumlusu",
};

export function Noktalar() {
  const [duzenlenen, setDuzenlenen] = useState<Nokta | null>(null);
  const [yeniAcik, setYeniAcik] = useState(false);
  const [atamaNoktasi, setAtamaNoktasi] = useState<Nokta | null>(null);

  const sorgu = useQuery({
    queryKey: ["nokta", "tumu"],
    queryFn: () => apiOku<{ noktalar: Nokta[] }>("/nokta"),
  });

  const noktalar = sorgu.data?.noktalar ?? [];

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Satış noktaları"
        aciklama="Nokta tanımı, cüzdan türü, hakediş oranı, fiş politikası ve personel atamaları."
        eylem={
          <Buton
            tur="birincil"
            onClick={() => {
              setYeniAcik((a) => !a);
              setDuzenlenen(null);
            }}
            aria-expanded={yeniAcik}
          >
            {yeniAcik ? "Formu kapat" : "Yeni nokta"}
          </Buton>
        }
      />

      <Uyari tur="bilgi" baslik="Depo satış yapmaz (AK-19)">
        <p className="m-0">
          Depo türündeki nokta satış yapamaz ve{" "}
          <strong>cüzdan türü alamaz</strong>; ona terminal de bağlanamaz. Depo,
          malın okula girdiği ve noktalara dağıtıldığı yerdir — kasa değil.
        </p>
        <p className="m-0 mt-1">
          Terminali olan bir noktayı satış dışı bırakmak da reddedilir: önce
          terminalleri başka noktaya taşıyın ya da iptal edin. Sessizce
          bırakılsa, terminaller sahipsiz kalırdı.
        </p>
      </Uyari>

      {yeniAcik && (
        <NoktaFormu
          nokta={null}
          kapat={() => setYeniAcik(false)}
          kaydedildi={() => {
            setYeniAcik(false);
            void sorgu.refetch();
          }}
        />
      )}

      {duzenlenen && (
        <NoktaFormu
          nokta={duzenlenen}
          kapat={() => setDuzenlenen(null)}
          kaydedildi={() => {
            setDuzenlenen(null);
            void sorgu.refetch();
          }}
        />
      )}

      <Kart baslik="Noktalar">
        <SorguDurumu
          yukleniyor={sorgu.isPending}
          hata={sorgu.error}
          bos={noktalar.length === 0}
          bosBaslik="Nokta tanımlanmamış"
          bosAciklama="Satışın yapılabilmesi için en az bir satış noktası ve mal girişi için bir depo noktası gereklidir."
          yenile={() => void sorgu.refetch()}
        >
          <Tablo>
            <TabloBaslik>
              <th scope="col">Nokta</th>
              <th scope="col">Tür</th>
              <th scope="col">Cüzdan</th>
              <th scope="col">Hakediş</th>
              <th scope="col">Fiş politikası</th>
              <th scope="col">Sorumlu</th>
              <th scope="col">Terminal</th>
              <th scope="col">
                <span className="gizli-erisilebilir">İşlem</span>
              </th>
            </TabloBaslik>
            <TabloGovde>
              {noktalar.map((n) => (
                <tr key={n.id}>
                  <td>
                    <p className="m-0 font-semibold text-murekkep">{n.ad}</p>
                    {!n.aktif && <Rozet tur="notr">pasif</Rozet>}
                  </td>
                  <td className="text-sm text-murekkep-2">
                    {NOKTA_TURU_ADI[n.tur] ?? "Bilinmiyor"}
                    {!n.satis_yapar && (
                      <span className="block text-xs text-murekkep-3">
                        satış yapmaz
                      </span>
                    )}
                  </td>
                  <td className="text-sm text-murekkep-2">
                    {n.cuzdan_turu === null
                      ? "—"
                      : (CUZDAN_TURU_ADI[n.cuzdan_turu] ?? "—")}
                  </td>
                  <td className="rakam text-right font-mono text-sm">
                    {n.hakedis_orani === null ? "—" : `%${n.hakedis_orani}`}
                  </td>
                  <td className="text-sm text-murekkep-2">
                    {FIS_POLITIKASI_ADI[n.fis_politikasi] ?? "—"}
                  </td>
                  <td className="text-sm text-murekkep-2">
                    {n.sorumlu_ad ?? "—"}
                  </td>
                  <td className="rakam text-right font-mono text-sm">
                    {n.terminal_sayisi}
                  </td>
                  <td className="text-right">
                    <span className="flex flex-wrap justify-end gap-1">
                      <Buton
                        tur="sessiz"
                        onClick={() => {
                          setDuzenlenen(n);
                          setYeniAcik(false);
                        }}
                      >
                        Düzenle
                      </Buton>
                      <Buton
                        tur="sessiz"
                        onClick={() =>
                          setAtamaNoktasi(atamaNoktasi?.id === n.id ? null : n)
                        }
                        aria-expanded={atamaNoktasi?.id === n.id}
                      >
                        Atamalar
                      </Buton>
                    </span>
                  </td>
                </tr>
              ))}
            </TabloGovde>
          </Tablo>
        </SorguDurumu>
      </Kart>

      {atamaNoktasi && <NoktaAtamalari nokta={atamaNoktasi} />}
    </Sayfa>
  );
}

// ---------------------------------------------------------------------------

function NoktaFormu({
  nokta,
  kapat,
  kaydedildi,
}: {
  nokta: Nokta | null;
  kapat: () => void;
  kaydedildi: () => void;
}) {
  const personeller = usePersoneller({ aktif: true });
  const [ad, setAd] = useState(nokta?.ad ?? "");
  const [tur, setTur] = useState(String(nokta?.tur ?? NoktaTuru.KANTIN));
  const [satisYapar, setSatisYapar] = useState(nokta?.satis_yapar ?? true);
  const [cuzdanTuru, setCuzdanTuru] = useState(
    nokta?.cuzdan_turu === null || nokta?.cuzdan_turu === undefined
      ? String(CuzdanTuru.KANTIN)
      : String(nokta.cuzdan_turu),
  );
  const [sorumlu, setSorumlu] = useState(nokta?.sorumlu_personel_id ?? "");
  const [hakedis, setHakedis] = useState(nokta?.hakedis_orani ?? "");
  const [fisPolitikasi, setFisPolitikasi] = useState(
    String(nokta?.fis_politikasi ?? FisPolitikasi.KAPALI),
  );
  const [aktif, setAktif] = useState(nokta?.aktif ?? true);
  const [gonderildi, setGonderildi] = useState(false);

  const depoMu = Number.parseInt(tur, 10) === NoktaTuru.DEPO;
  // AK-19: depo seçilir seçilmez satış kapanır ve cüzdan türü düşürülür.
  const gercekSatisYapar = depoMu ? false : satisYapar;

  const kaydet = useMutation({
    mutationFn: () => {
      const govde = {
        ad: ad.trim(),
        tur: Number.parseInt(tur, 10),
        satis_yapar: gercekSatisYapar,
        cuzdan_turu: gercekSatisYapar ? Number.parseInt(cuzdanTuru, 10) : null,
        sorumlu_personel_id: sorumlu || null,
        hakedis_orani: hakedis.trim() ? hakedis.trim().replace(",", ".") : null,
        fis_politikasi: Number.parseInt(fisPolitikasi, 10),
        aktif,
      };
      return nokta
        ? apiYaz<Nokta>("PATCH", `/nokta/${nokta.id}`, govde)
        : apiYaz<Nokta>("POST", "/nokta", govde);
    },
    onSuccess: kaydedildi,
  });

  const adHatasi =
    gonderildi && !ad.trim() ? "Nokta adı zorunludur." : undefined;
  const hakedisHatasi =
    hakedis.trim() &&
    !/^(100([.,]0{1,2})?|\d{1,2}([.,]\d{1,2})?)$/.test(hakedis.trim())
      ? "Hakediş oranı 0 ile 100 arasında olmalıdır (örn. 12,50)."
      : undefined;

  return (
    <Kart baslik={nokta ? `Nokta düzenle: ${nokta.ad}` : "Yeni nokta"}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setGonderildi(true);
          if (!ad.trim() || hakedisHatasi) return;
          kaydet.mutate();
        }}
      >
        <div className="grid gap-3 md:grid-cols-3">
          <Alan
            etiket="Nokta adı"
            value={ad}
            onChange={(e) => setAd(e.target.value)}
            {...(adHatasi ? { hata: adHatasi } : {})}
          />
          <Secim
            etiket="Tür"
            value={tur}
            onChange={(e) => setTur(e.target.value)}
            secenekler={Object.entries(NOKTA_TURU_ADI).map(([k, v]) => ({
              deger: k,
              metin: v,
            }))}
          />
          <Secim
            etiket="Cüzdan türü"
            value={gercekSatisYapar ? cuzdanTuru : ""}
            disabled={!gercekSatisYapar}
            onChange={(e) => setCuzdanTuru(e.target.value)}
            secenekler={
              gercekSatisYapar
                ? Object.entries(CUZDAN_TURU_ADI).map(([k, v]) => ({
                    deger: k,
                    metin: v,
                  }))
                : [{ deger: "", metin: "Yok (satış yapmayan nokta)" }]
            }
          />
        </div>

        {depoMu ? (
          <Uyari tur="uyari" baslik="Depo noktası">
            Bu nokta satış yapamaz ve cüzdan türü taşıyamaz. Mal girişi buraya
            yapılır, satış noktalarına transferle dağıtılır.
          </Uyari>
        ) : (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={satisYapar}
              onChange={(e) => setSatisYapar(e.target.checked)}
              className="h-4 w-4 accent-simit"
            />
            Bu noktada satış yapılır
          </label>
        )}

        <div className="grid gap-3 md:grid-cols-3">
          <Secim
            etiket="Sorumlu personel"
            value={sorumlu}
            onChange={(e) => setSorumlu(e.target.value)}
            secenekler={[
              { deger: "", metin: "Atanmadı" },
              ...(personeller.data?.personeller ?? []).map((p) => ({
                deger: p.id,
                metin: `${p.ad} ${p.soyad}`,
              })),
            ]}
          />
          <Alan
            etiket="Hakediş oranı (%)"
            value={hakedis}
            onChange={(e) => setHakedis(e.target.value)}
            inputMode="decimal"
            ipucu="Nokta bazlı hakediş raporunun kaynağı. Boş ise hakediş hesaplanmaz."
            {...(hakedisHatasi ? { hata: hakedisHatasi } : {})}
          />
          <Secim
            etiket="Fiş politikası"
            value={fisPolitikasi}
            onChange={(e) => setFisPolitikasi(e.target.value)}
            secenekler={Object.entries(FIS_POLITIKASI_ADI).map(([k, v]) => ({
              deger: k,
              metin: v,
            }))}
          />
        </div>

        <p className="m-0 text-xs text-murekkep-3">
          Fiş politikası &quot;opsiyonel&quot; iken kasadaki görevli fiş
          anahtarını kullanabilir; &quot;kapalı&quot; ve &quot;zorunlu&quot;
          durumlarında anahtar devre dışıdır.
        </p>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={aktif}
            onChange={(e) => setAktif(e.target.checked)}
            className="h-4 w-4 accent-simit"
          />
          Nokta aktif
        </label>

        <IslemDurumu hata={kaydet.error} />

        <div className="flex flex-wrap gap-2">
          <Buton type="submit" tur="birincil" yukleniyor={kaydet.isPending}>
            {nokta ? "Değişiklikleri kaydet" : "Noktayı kaydet"}
          </Buton>
          <Buton type="button" tur="ikincil" onClick={kapat}>
            Vazgeç
          </Buton>
        </div>
      </form>
    </Kart>
  );
}

// ---------------------------------------------------------------------------
// KK-1507 / KK-522 · Personel atamaları ve vekalet
// ---------------------------------------------------------------------------

function NoktaAtamalari({ nokta }: { nokta: Nokta }) {
  const istemci = useQueryClient();
  const [personelId, setPersonelId] = useState("");
  const [rol, setRol] = useState("1");
  const [vekaletMi, setVekaletMi] = useState(false);
  const [baslangic, setBaslangic] = useState("");
  const [bitis, setBitis] = useState("");
  const [gonderildi, setGonderildi] = useState(false);

  const atananlar = useQuery({
    queryKey: ["personel", "nokta", nokta.id],
    queryFn: () =>
      apiOku<{ personeller: Personel[] }>("/personel", { nokta_id: nokta.id }),
  });
  const tumPersonel = usePersoneller({ aktif: true });

  const tazele = () => {
    void istemci.invalidateQueries({ queryKey: ["personel"] });
  };

  const ata = useMutation({
    mutationFn: () =>
      apiYaz("POST", `/personel/${personelId}/nokta`, {
        nokta_id: nokta.id,
        rol: Number.parseInt(rol, 10),
        baslangic: vekaletMi && baslangic ? baslangic : null,
        bitis: vekaletMi && bitis ? bitis : null,
      }),
    onSuccess: () => {
      setPersonelId("");
      setVekaletMi(false);
      setBaslangic("");
      setBitis("");
      setGonderildi(false);
      tazele();
    },
  });

  const kaldir = useMutation({
    mutationFn: (pid: string) =>
      apiYaz("DELETE", `/personel/${pid}/nokta/${nokta.id}`),
    onSuccess: tazele,
  });

  const satirlar = (atananlar.data?.personeller ?? []).flatMap((p) =>
    (p.noktalar ?? [])
      .filter((a) => a.nokta_id === nokta.id)
      .map((a) => ({ personel: p, atama: a })),
  );

  const personelHatasi =
    gonderildi && !personelId ? "Personel seçin." : undefined;
  const tarihHatasi =
    vekaletMi && baslangic && bitis && bitis < baslangic
      ? "Vekalet bitiş tarihi başlangıçtan önce olamaz."
      : undefined;

  return (
    <Kart baslik={`Personel atamaları: ${nokta.ad}`}>
      <div className="flex flex-col gap-4">
        <SorguDurumu
          yukleniyor={atananlar.isPending}
          hata={atananlar.error}
          bos={satirlar.length === 0}
          bosBaslik="Bu noktaya personel atanmamış"
          bosAciklama="Atanmamış noktada kasiyer PIN ile oturum açamaz."
          iskeletSatir={3}
          yenile={() => void atananlar.refetch()}
        >
          <Tablo>
            <TabloBaslik>
              <th scope="col">Personel</th>
              <th scope="col">Atama rolü</th>
              <th scope="col">Vekalet aralığı</th>
              <th scope="col">Durum</th>
              <th scope="col">
                <span className="gizli-erisilebilir">İşlem</span>
              </th>
            </TabloBaslik>
            <TabloGovde>
              {satirlar.map(({ personel, atama }) => (
                <tr key={`${personel.id}-${atama.nokta_id}`}>
                  <td className="font-semibold text-murekkep">
                    {personel.ad} {personel.soyad}
                  </td>
                  <td className="text-sm text-murekkep-2">
                    {ATAMA_ROLU_ADI[atama.rol] ?? "—"}
                  </td>
                  <td className="text-sm text-murekkep-2">
                    {atama.vekalet
                      ? `${tarihGoster(atama.baslangic)} – ${tarihGoster(atama.bitis)}`
                      : "Sürekli"}
                  </td>
                  <td>
                    {atama.gecerli ? (
                      <Rozet tur="onay">geçerli</Rozet>
                    ) : (
                      <Rozet tur="notr">süresi dışında</Rozet>
                    )}
                  </td>
                  <td className="text-right">
                    <Buton
                      tur="sessiz"
                      onClick={() => kaldir.mutate(personel.id)}
                      yukleniyor={kaldir.isPending}
                    >
                      Kaldır
                    </Buton>
                  </td>
                </tr>
              ))}
            </TabloGovde>
          </Tablo>
        </SorguDurumu>

        <IslemDurumu hata={kaldir.error} />

        <form
          className="flex flex-col gap-3 border-t border-cetvel pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            setGonderildi(true);
            if (!personelId || tarihHatasi) return;
            ata.mutate();
          }}
        >
          <h4 className="m-0 text-lg">Yeni atama</h4>
          <div className="grid gap-3 md:grid-cols-2">
            <Secim
              etiket="Personel"
              value={personelId}
              onChange={(e) => setPersonelId(e.target.value)}
              secenekler={[
                { deger: "", metin: "Seçiniz" },
                ...(tumPersonel.data?.personeller ?? []).map((p) => ({
                  deger: p.id,
                  metin: `${p.ad} ${p.soyad}`,
                })),
              ]}
            />
            <Secim
              etiket="Atama rolü"
              value={rol}
              onChange={(e) => setRol(e.target.value)}
              secenekler={Object.entries(ATAMA_ROLU_ADI).map(([k, v]) => ({
                deger: k,
                metin: v,
              }))}
            />
          </div>
          {personelHatasi && (
            <p className="m-0 text-xs text-hata" role="alert">
              {personelHatasi}
            </p>
          )}

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={vekaletMi}
              onChange={(e) => setVekaletMi(e.target.checked)}
              className="h-4 w-4 accent-simit"
            />
            Bu bir vekalet (tarihli geçici atama)
          </label>

          {vekaletMi && (
            <div className="grid gap-3 md:grid-cols-2">
              <Alan
                etiket="Başlangıç"
                type="date"
                value={baslangic}
                onChange={(e) => setBaslangic(e.target.value)}
              />
              <Alan
                etiket="Bitiş"
                type="date"
                value={bitis}
                onChange={(e) => setBitis(e.target.value)}
                ipucu="Bitiş dolunca atama kendiliğinden düşer; ayrıca kaldırmanız gerekmez."
                {...(tarihHatasi ? { hata: tarihHatasi } : {})}
              />
            </div>
          )}

          <IslemDurumu hata={ata.error} />

          <div>
            <Buton type="submit" tur="birincil" yukleniyor={ata.isPending}>
              Atamayı kaydet
            </Buton>
          </div>
        </form>
      </div>
    </Kart>
  );
}
